import { QueryClient } from "@tanstack/react-query";
import { isThrottled, retryAfterMs } from "src/lib/throttling.ts";

/**
 * Задросселенный запрос повторяем один раз и только если ждать недолго.
 *
 * Сервер в `Retry-After` называет срок до сброса окна — он доходит до минуты.
 * Молча висеть скелетоном столько времени хуже, чем честно сказать, сколько
 * осталось: длинное ожидание отдаём экрану `TooManyRequests` с обратным
 * отсчётом, коротким всплеском занимаемся сами.
 */
const THROTTLE_RETRIES = 1;

const SHORT_WAIT_MS = 10_000;

const retryThrottled = (failureCount: number, error: unknown): boolean =>
  isThrottled(error) &&
  failureCount < THROTTLE_RETRIES &&
  retryAfterMs(error) <= SHORT_WAIT_MS;

export const createQueryClient = () => {
  // const mutationCache = new MutationCache({
  //   onSuccess: () => {
  //     queryClient.invalidateQueries();
  //   },
  // });
  const queryClient = new QueryClient({
    // mutationCache,
    defaultOptions: {
      queries: {
        refetchOnReconnect: true,
        refetchOnWindowFocus: false,
        // We don't want to retry queries by default, because in most cases we
        // want to fail early and show a response to the user. There are
        // exceptions, and those can be made on a per-query basis. For others, we
        // should give users controls to retry.
        //
        // Исключение — 429: сервер не отказал по сути, а попросил подождать.
        // Без повтора один задросселенный запрос убивал вкладку целиком.
        retry: retryThrottled,
        retryDelay: (_failureCount, error) => retryAfterMs(error),
        refetchOnMount: false,
      },
      mutations: {
        // Троттлинг отбивает запрос до контроллера — сохранение не началось,
        // и повтор не создаст второй записи.
        retry: retryThrottled,
        retryDelay: (_failureCount, error) => retryAfterMs(error),
      },
    },
  });
  return queryClient;
};

export const queryClient = createQueryClient();
